/* The self check-in queue, as plain functions over plain arrays.
 *
 * Two questions live here, and they are the two the lobby tablet and the front
 * desk disagree about most easily:
 *
 *   what the tablet may offer   given what the board says about a service, is
 *                               this a check-in, a place in a queue, or a
 *                               sentence pointing at the desk?
 *   what order the queue is in  who has been waiting longest, and what number
 *                               is somebody when they are told one?
 *
 * Both are here rather than in store.js for the reason turns.js and queue.js
 * are: they are the part that has to be right, and they can be asserted without
 * standing a Supabase client up. The order one especially — the whole promise
 * this feature makes to a client is "you are third and the desk knows it", and
 * a sort that quietly disagrees between the tablet's confirmation screen and
 * the desk's list breaks that promise in the one direction nobody would notice
 * until somebody complained.
 */

// What the client actually typed, made safe to store and to read back. Newlines
// and runs of spaces collapse — a tablet keyboard and a tired thumb produce
// both — and the cap is a column width, not a judgement about names.
export function cleanName(raw) {
  return String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim().slice(0, 60);
}

/* Today's queue: the ones still waiting, oldest first.
 *
 * `createdAt` is stamped by whichever device took the check-in, so two tablets
 * with clocks a second apart can produce a tie. Ties break on `id`, which is
 * arbitrary but *stable* — without it two screens sorting the same array can
 * disagree about who is third, which is exactly the disagreement this file
 * exists to prevent.
 */
export function waitingRows(rows) {
  return (rows || [])
    .filter(function (r) { return r && r.status === 'waiting'; })
    .slice()
    .sort(function (a, b) {
      return (a.createdAt - b.createdAt) || String(a.id).localeCompare(String(b.id));
    });
}

// Where somebody stands in that queue, counting from one — the number the
// tablet says out loud. Zero means they are not in it, which is what the desk
// having already seated them looks like from the tablet's side.
export function positionOf(rows, id) {
  var i = waitingRows(rows).findIndex(function (r) { return r.id === id; });
  return i === -1 ? 0 : i + 1;
}

// How long they have been standing there. Never negative: a tablet whose clock
// runs a minute fast would otherwise put "-1 min" on the desk's screen.
export function waitedMinutes(entry, now) {
  if (!entry || entry.createdAt == null) return 0;
  return Math.max(0, Math.floor((now - entry.createdAt) / 60000));
}

// The same number as a label. Under a minute is "just now" rather than "0 min",
// which reads as a broken counter; past an hour it breaks into hours, because
// "78 min" is a number somebody has to do arithmetic on to be shocked by.
export function waitLabel(minutes) {
  if (minutes < 1) return 'just now';
  if (minutes < 60) return minutes + ' min';
  var h = Math.floor(minutes / 60);
  var m = minutes % 60;
  return h + 'h' + (m ? ' ' + m + 'm' : '');
}

/* What the tablet may offer for a service.
 *
 * `check` is `store.serviceAvailability` — the same answer the board's own
 * service menu is armed from — and reusing it is the point rather than a
 * convenience. A second implementation of "is anybody available for this" on
 * the tablet would drift from the first within a month, and the way that
 * surfaces is the lobby sending somebody to a chair the desk knows is spoken
 * for.
 *
 * `hasRoom` is the second half, and it is why this takes two arguments instead
 * of one. `serviceAvailability` says yes as soon as somebody is free THIS
 * SECOND, which is right for the board: the next thing that happens there is a
 * person deciding, who can see that Kim's four o'clock is twenty minutes off
 * and that this is a fifteen-minute service. It is not that sheet's business to
 * refuse.
 *
 * There is nobody to decide in a lobby. So a check-in needs somebody free AND
 * with room in the book for the whole of the work — `availableWithRoom` in the
 * store, which measures each technician's next clear run against what the
 * service usually takes. Without it the tablet seats a ninety-minute set on
 * somebody whose own client walks in at half past, which is the one mistake the
 * desk cannot recover from, because the walk-in is already in the chair.
 *
 * Free-but-not-for-long-enough therefore reads as `busy` here and is joinable.
 * That is not the tablet overruling the desk: every one of those options is
 * still on the board, under the same service button it always was. It is a
 * machine acting unattended declining to make the call.
 *
 *   now      somebody can take it, and finish it. The check-in puts a square
 *            up, exactly as pressing that service on the board would.
 *   busy     everyone who does it is mid-service, out, or free without room.
 *            This is the case the waiting list exists for.
 *   none     nobody in today performs it at all. Still joinable, deliberately:
 *            it is usually a rota gap rather than a service the salon dropped,
 *            and a name on a list is how the desk gets the chance to say so.
 *            The alternative is a dead end at the tablet and a client who
 *            leaves without anybody knowing they came.
 *   closing  the work would not finish before the doors shut — by more than the
 *            board's grace, so it is not a few minutes over. Not joinable — a
 *            queue nobody can be taken off before closing is a promise the
 *            salon cannot keep, and how late is a decision that belongs to
 *            whoever would have to stay. The screen points at the desk instead.
 *
 *            Whether anybody is free this second does not change that. Free and
 *            too short a day arrives as `reason`; nobody free and a wait that
 *            also runs out of day arrives as `late` under `busy`, because the
 *            desk's own warning sheet wants those two apart — it says different
 *            paragraphs for them. To a client at the door they are one fact:
 *            there is no availability for this today.
 *   book     the wait is longer than the salon is willing to ask anybody to
 *            stand there for, and there is somewhere to send them instead. See
 *            `tooLongToWait`.
 *
 * `book` is the third argument and it is optional: absent, this behaves exactly
 * as it did before there was a booking link, which is what every salon that has
 * not set one up still gets. It is
 *
 *   { url, afterMinutes, waitMinutes }
 *
 * — the address for THIS service (resolved by js/rules/booking.js, because a
 * per-service link is not this file's business), the salon's cutoff, and what
 * the board thinks the wait actually is.
 */
export function checkinOffer(check, hasRoom, book) {
  if (!check) return { kind: 'none', canJoin: true };
  if (check.ok) {
    if (hasRoom) return { kind: 'now', canJoin: false };
    return tooLongToWait(book) || { kind: 'busy', canJoin: true };
  }
  /* Ahead of the cutoff, and deliberately. Closing is not a long wait — it is a
   * day that has run out, with somebody standing free in front of a client — and
   * it already sends them to the desk, which is a person who can offer them a
   * time and a great deal else besides. Sending them to a booking site instead
   * would replace the salon's best answer with its second best. */
  if (check.reason === 'closing' || pastClosing(check)) {
    return { kind: 'closing', canJoin: false };
  }
  return tooLongToWait(book) ||
    { kind: check.reason === 'none' ? 'none' : 'busy', canJoin: true };
}

/* The day has run out for this service, however the board phrased it.
 *
 * `late` is the board's overrun — when it would finish, when the doors shut, and
 * `blocked` for "further over than the grace". Only the blocked ones count: a few
 * minutes past closing is a question for a technician, and the board deliberately
 * still offers those. `blocked` is asked for rather than assumed because it is
 * the whole distinction, and a rules file that took any overrun for a refusal
 * would quietly turn every ten-past-seven finish into a trip to the desk.
 */
function pastClosing(check) {
  return !!(check.late && check.late.blocked);
}

/* Whether this is a person to send away to book, or one to put on the list.
 *
 * Three things all have to be true, and the first is the one that keeps this
 * feature from ever surprising anybody: there must be a LINK. A screen that
 * tells somebody to book an appointment and gives them no way to do it is worse
 * than the waiting list it replaced — a client who would have left their name
 * instead leaves the salon, and nobody at the desk ever knows they came. So with
 * no link the cutoff does nothing at all, whatever it is set to.
 *
 * The second is a cutoff: null means the salon has one link for the tile notes
 * and still wants everybody offered a place in the queue.
 *
 * The third is an estimate to compare it against. A service nobody in today
 * performs has no estimate — there is no rotation to measure — and that case
 * stays on the list on purpose: it is usually a rota gap, and a name on a list
 * is how the desk gets the chance to say "she is in tomorrow at ten". An
 * unknown wait is not a long one.
 *
 * `>=` rather than `>`: a salon that types 60 means "an hour or more", not "an
 * hour and one minute".
 */
function tooLongToWait(book) {
  if (!book || !book.url) return null;
  if (book.afterMinutes == null || book.waitMinutes == null) return null;
  return book.waitMinutes >= book.afterMinutes
    ? { kind: 'book', canJoin: false }
    : null;
}
